import React, { useCallback, useEffect, useRef, useState } from 'react';
import Select, { SelectItemRenderer, SelectRenderer } from 'react-dropdown-select';
import UserName from '../common/user/UserName';
import MageDotsIcon from '../svgIcons/MageDotsIcon';
import SearchInput from '../common/search-input/SearchInput';
import Button from '../common/button/Button';
import PlusIcon from '../svgIcons/PlusIcon';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import { useDispatch } from 'react-redux';
import { activateStaff, bulkImportStaff, clearExportStaffState, deactivateStaff, getExportStaff, getStaffList } from '../../redux/staff-management/action/staff.action';
import CustModal from '../common/custmodal/CustModal';
import { useSelector } from 'react-redux';
import { getFilteredLocation } from '../../redux/location/actions/location.action';
import { getRolesList } from '../../redux/roles/actions/roles.action';
import { LocationData } from '../locations/interfaces/locations.interface';
import { RoleProps, ToastType } from '../roles/Roles';
import Toast from '../common/toast/Toast';
import Loader from '../common/loader/Loader';
import { useDebounce } from '../../utils/useDebounce';
import { StaffModal } from './StaffModal';
import FilterIcon from '../svgIcons/FilterIcon';
import PropertiesModal from './PropertiesModal';
import styles from './StaffManagement.module.css';
import { Option } from '../auth/login/Login';
import TinyLoader from '../common/tiny-loader/TinyLoader';

interface StaffMember {
    id: string;
    name: string;
    email: string;
    role: string;
    locations: LocationListProps[];
    isActive: boolean;
}

export interface LocationListProps {
    id: string;
    name: string;
}

export interface StaffFormProps {
    fullName: string;
    email: string;
    locationId: string[];
    roleId: string;
    adminId: string;
    staffId: null;
}

interface InvitedBy {
    id: string;
    name: string;
    email: string;
    role: string;
    locations: { locationId: string; locationName: string }[];
}

interface StaffAPIResponse {
    id: string;
    name: string;
    email: string;
    role: string;
    invitedBy: InvitedBy[];
    isActive: boolean;
}

export interface StaffUpdate {
    staffId: string;
    locationIdsToAdd: string[];
    locationIdsToRemove: string[];
    roleId: string;
    fullName: string;
}
export interface FailedImportData {
    email: string;
    reason: string;
}

const StaffManagement = () => {
    const [showActionBox, setShowActionBox] = useState<string | null>(null);
    const [actionBoxPosition, setActionBoxPosition] = useState<'top' | 'bottom'>('bottom');
    const actionButtonRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showFilter, setShowFilter] = useState<boolean>(false);
    const [locationList, setLocationList] = useState<LocationListProps[]>([]);
    const [filterLocationList, setFilterLocationList] = useState<Option[]>([]);
    const [staffList, setStaffList] = useState<StaffMember[]>([]);
    const [filterRoleList, setFilterRoleList] = useState<Option[]>([]);
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState<ToastType>('success');
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const locationData = useSelector((state: IRootState) => state?.locationReducer?.data);
    const searchWithDebounce = useDebounce(searchTerm, 1000);
    const { data: rolesList } = useSelector((state: IRootState) => state.rolesListReducer);
    const {
        data: staffListData,
        count: totalCount,
        error: staffListError,
        loading: staffListLoading,
    } = useSelector((state: IRootState) => state?.staffManagementReducer);

    const { exportStaffMessage, exportStaffLoading, exportStaffError } = useSelector((state: IRootState) => state?.staffManagementReducer);

    const [currentPage, setCurrentPage] = useState(1);
    const observer = useRef<IntersectionObserver | null>(null);
    const tinyLoaderRef = useRef<HTMLDivElement | null>(null);
    const LIMIT = 12;
    const hasMore = staffListData?.length < totalCount;
    const [isAllPropertiesSelected, setIsAllPropertiesSelected] = useState(false);
    const [filterState, setFilterState] = useState<{ properties: string[]; roles: string[] }>({
        properties: [],
        roles: [],
    });

    // Properties modal state
    const [showPropertiesModal, setShowPropertiesModal] = useState(false);
    const [selectedStaffForProperties, setSelectedStaffForProperties] = useState<{
        id: string;
        name: string;
        roleName: string;
        email: string;
        assignedProperties: string[];
    } | null>(null);

    const [initialfilterState, setInitialFilterState] = useState<{ properties: string[]; roles: string[] }>({
        properties: [],
        roles: [],
    });

    const [bulkImportLoading, setBulkImportLoading] = useState(false);
    const [failedData, setFailedData] = useState<FailedImportData[]>([]);
    const [successCount, setSuccessCount] = useState(0);
    const [showImportResultModal, setShowImportResultModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setFailedData([]);

        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            onFailure('Please upload a valid CSV file.');
            event.target.value = '';
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setBulkImportLoading(true);
        dispatch(bulkImportStaff(
            formData,
            ( 
                successMessage: string,
                failedRows: FailedImportData[]
            ) => {
                setBulkImportLoading(false);
                // Parse success count from the message "Bulk import completed. Success: X, Failed: Y"
                const successMatch = successMessage.match(/Success:\s*(\d+)/);
                const sc = successMatch ? parseInt(successMatch[1], 10) : 0;
                setSuccessCount(sc);
                setFailedData(failedRows || []);
                setShowImportResultModal(true);
                getUpdatedStaffList();
                event.target.value = '';
            },
            (errorMessage) => {
                setBulkImportLoading(false);
                onFailure(errorMessage);
                event.target.value = '';
            }
        ));
    };

    const downloadFailedCSV = () => {
        if (!failedData.length) return;

        const headers = ['Email', 'Reason'];
        const rows = failedData.map(item => [
            item.email || '',
            `"${(item.reason || '').replace(/"/g, '""')}"`
        ]);

        const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `failed_staff_import_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setFailedData([]);
    };

    useEffect(() => {
        if (locationData?.statusCode === 200) {
            const invitedList = Array.isArray(locationData.locationinvitedlist)
                ? locationData.locationinvitedlist
                    .filter((item: LocationData) => !!item?.id && !!item?.name) // Filter valid objects
                    .map(({ id, name }: { id: string; name: string }) => ({ id, name }))
                : [];

            const locations = Array.isArray(locationData.locations)
                ? locationData.locations
                    .filter((item: LocationData) => !!item?.id && !!item?.name) // Filter valid objects
                    .map(({ id, name }: { id: string; name: string }) => ({ id, name }))
                : [];
            setLocationList([...invitedList, ...locations]);
            setFilterLocationList(
                [...invitedList, ...locations].map((location: { id: string; name: string }) => ({
                    label: location.name,
                    value: location.id,
                })),
            );
        } else {
            setLocationList([]);
        }
    }, [locationData]);

    const dispatch: AppDispatch = useDispatch();

    useEffect(() => {
        dispatch(getFilteredLocation('', '', true));
        dispatch(getRolesList(null));
    }, []);

    useEffect(() => {
        setFilterRoleList([...(rolesList?.map((role: RoleProps) => ({ value: role.id, label: role.name })) || [])]);
    }, [rolesList]);

    useEffect(() => {
        return () => {
            dispatch(clearExportStaffState());
        };
    }, []);

    useEffect(() => {
        setCurrentPage(1);
        dispatch(getStaffList(searchWithDebounce || null, filterState?.roles, filterState?.properties, 1, LIMIT, false));
    }, [searchWithDebounce]);

    const lastElement = (node: HTMLTableRowElement | null) => {
        if (staffListLoading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    if (currentPage * LIMIT < totalCount) {
                        setCurrentPage((prevPage) => prevPage + 1);
                    }
                }
            },
            {
                threshold: 0.9, // Trigger when 90% of the element is visible
            }
        );
        if (node) observer.current.observe(node);
    };

    useEffect(() => {
        if (currentPage > 1) {
            dispatch(getStaffList(searchWithDebounce || null, filterState.roles, filterState.properties, currentPage, LIMIT, true));
        }
    }, [currentPage]);

    useEffect(() => {
        if (staffListLoading && currentPage > 1 && tinyLoaderRef.current) {
            setTimeout(() => {
                tinyLoaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 100);
        }
    }, [staffListLoading, currentPage]);

    const handleActionButtonClick = (index: string) => {
        if (showActionBox === index) {
            setShowActionBox(null);
        } else {
            setShowActionBox(index);
            const currentRow = actionButtonRefs.current[index];
            if (currentRow) {
                const rect = currentRow.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const spaceBelow = windowHeight - rect.bottom;
                const spaceAbove = rect.top;
                setActionBoxPosition(spaceBelow < 200 && spaceAbove > 200 ? 'top' : 'bottom');
            }
        }
    };

    const handleShowProperties = (staff: StaffMember) => {
        const assignedProperties = staff.locations?.map((location) => location.name) || [];
        setSelectedStaffForProperties({
            id: staff.id,
            name: staff.name,
            roleName: staff.role,
            email: staff.email,
            assignedProperties,
        });
        setShowPropertiesModal(true);
    };

    useEffect(() => {
        if (showToast) {
            setTimeout(() => {
                setShowToast(false);
            }, 4000);
        }
    }, [showToast]);

    useEffect(() => {
        if (exportStaffMessage) {
            setToastMessage(exportStaffMessage);
            setToastType('success');
            setShowToast(true);
            dispatch(clearExportStaffState());
        }
    }, [exportStaffMessage]);

    useEffect(() => {
        if (exportStaffError) {
            onFailure(exportStaffError);
            dispatch(clearExportStaffState());
        }
    }, [exportStaffError]);

    useEffect(() => {
        if (staffListData && !staffListError) {
            const transformedData: StaffMember[] = staffListData?.map((staff: StaffAPIResponse) => ({
                id: staff.id,
                name: staff.name,
                email: staff.email,
                role: staff.role,
                isActive: staff?.isActive || false,
                locations: staff.invitedBy.flatMap((inviter: InvitedBy) =>
                    inviter.locations.map((loc) => ({ id: loc.locationId, name: loc.locationName })),
                ),
            }));
            setStaffList(transformedData);
        } else if (staffListError) {
            setStaffList([]);
        }
    }, [staffListData, staffListError]);

    const onSuccess = (message: string) => {
        setToastMessage(message);
        setToastType('success');
        setShowToast(true);
        getUpdatedStaffList();
    };

    const onFailure = (message: string) => {
        setToastMessage(message);
        setToastType('error');
        setShowToast(true);
    };

    const deactivate = (id: string) => {
        dispatch(deactivateStaff(id, onSuccess, onFailure));
    };

    const activate = (id: string) => {
        dispatch(activateStaff(id, onSuccess, onFailure));
    };

    const getUpdatedStaffList = () => {
        setCurrentPage(1);
        dispatch(getStaffList(searchWithDebounce || null, filterState.roles, filterState.properties, 1, LIMIT, false));
    };

    const onCancel = () => {
        setShowFilter(false);
        setFilterState(initialfilterState);
    };

    const onClear = async () => {
        const applied =
            JSON.stringify(initialfilterState) !==
            JSON.stringify({
                properties: [],
                roles: [],
            });
        await setFilterState({
            properties: [],
            roles: [],
        });
        await setInitialFilterState({
            properties: [],
            roles: [],
        });
        setShowFilter(false);
        setCurrentPage(1);
        applied && dispatch(getStaffList(searchWithDebounce || null, [], [], 1, LIMIT, false));
    };

    const onFilter = () => {
        setShowFilter(false);
        setInitialFilterState(filterState);
        getUpdatedStaffList();
    };

    const customContentRenderer = ({ props, state, methods }: SelectRenderer<Option>) =>
        state.values?.length ? (
            <div style={{ cursor: 'pointer' }}>
                {state.values.length} of {props.options.length} Properties selected
            </div>
        ) : (
            <div className={styles.placeholder}>
                <span>Select Properties</span>
            </div>
        );

    const customRoleContentRenderer = ({ props, state, methods }: SelectRenderer<Option>) =>
        state.values?.length ? (
            <div style={{ cursor: 'pointer' }}>
                {state.values.length} of {props.options.length} Roles selected
            </div>
        ) : (
            <div className={styles.placeholder}>
                <span>Select Roles</span>
            </div>
        );

    const itemRenderer = (props: SelectItemRenderer<Option>) => {
        const { item } = props;
        return (
            <div className={styles.boxes}>
                <input type="checkbox" id={item?.label} />
                <label htmlFor={item?.label}>{item?.value}</label>
            </div>
        );
    };

    const customDropdownRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
        const regexp = new RegExp(state.search, 'i');

        if (state?.values?.length !== props?.options?.length) {
            setIsAllPropertiesSelected(false);
        }
        if (state?.values?.length === props?.options?.length) {
            setIsAllPropertiesSelected(true);
        }

        return (
            <div className={styles.customDropdownMenu}>
                <div className={styles.inlineItems}>
                    <div className={styles.boxes}>
                        <input
                            type="checkbox"
                            id="selectAll"
                            checked={isAllPropertiesSelected}
                            onChange={(e) => {
                                setIsAllPropertiesSelected(e.target.checked);
                                e.target.checked ? methods.selectAll() : methods.clearAll();
                            }}
                        />
                        <label htmlFor="selectAll">Select All</label>
                    </div>
                    <button className={styles.clearBtn} onClick={() => methods.clearAll()}>
                        Clear All
                    </button>
                </div>

                {/* List of Checkboxes */}
                <div className={styles.listContainer}>
                    {props.options
                        .filter((item: Option) => regexp.test(item.label))
                        .map((option: Option) => {
                            if (!props.keepSelectedInList && methods.isSelected(option)) {
                                return null;
                            }

                            return (
                                <div key={option.value} className={styles.boxes}>
                                    <input
                                        type="checkbox"
                                        id={`option-${option.value}`}
                                        checked={methods.isSelected(option)}
                                        onChange={() => methods.addItem(option)}
                                    />
                                    <label htmlFor={`option-${option.value}`}>{option.label}</label>
                                </div>
                            );
                        })}
                </div>
            </div>
        );
    };

    const exportStaff = useCallback(() => {
        dispatch(getExportStaff());
    }, [dispatch]);

    useEffect(() => {
        showFilter && setInitialFilterState(filterState);
    }, [showFilter]);

    return (
        <div className={styles.pageContainer}>
            {(staffListLoading && staffListData?.length === 0 || exportStaffLoading || bulkImportLoading) && <Loader />}
            <div className={styles.pageHeader}>
                <div className={styles.userGreet}>
                    <p>
                        Welcome <UserName />,
                    </p>
                    <h2>Staff Management</h2>
                </div>

                <div className={styles.rightContainer}>

                    {userInfo && userInfo.role === 'super_admin' && (
                        <>
                            <input
                                type="file"
                                accept=".csv"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <Button
                                variant="outlined-green"
                                disabled={bulkImportLoading || exportStaffLoading}
                                onClick={handleUploadClick}
                            >
                                <span>{'Bulk Import Staff'}</span>
                            </Button>



                            <Button
                                variant="green"
                                disabled={exportStaffLoading || bulkImportLoading}
                                onClick={() => {
                                    exportStaff();
                                }}>
                                <span>{'Export Staff List'}</span>
                            </Button>
                        </>
                    )}

                    <div className={styles.filterWrapper}>
                        {
                            <button className={styles.filterIconWrapper} onClick={() => setShowFilter(!showFilter)}>
                                <FilterIcon />
                                {JSON.stringify(initialfilterState) !==
                                    JSON.stringify({
                                        properties: [],
                                        roles: [],
                                    }) && <span className={styles.active}></span>}
                            </button>
                        }
                        {showFilter && <div className={styles.filterBackDrop} onClick={onCancel}></div>}
                        {showFilter && (
                            <div className={`${styles.filterMenuWrapper} ${showFilter ? styles.showFilter : ''}`}>
                                <div className={styles.dropDownWrapper}>
                                    <Select
                                        className={styles.customReactSelectDropdown}
                                        options={filterLocationList || []}
                                        multi
                                        onSelect={() => null}
                                        onDeselect={() => null}
                                        wrapperClassName={styles.customReactSelectInput}
                                        closeOnClickInput
                                        values={filterLocationList?.filter((item) => filterState?.properties?.includes(item.value)) || []}
                                        keepSelectedInList
                                        onChange={(values: Option[]) => {
                                            if (values) {
                                                setFilterState({
                                                    ...filterState,
                                                    properties: values.map((value: Option) => value.value),
                                                });
                                            }
                                        }}
                                        searchBy="label"
                                        clearable={false}
                                        contentRenderer={customContentRenderer}
                                        itemRenderer={itemRenderer}
                                        dropdownRenderer={customDropdownRenderer}
                                    // onSelect={() => undefined}
                                    // onDeselect={() => undefined}
                                    />
                                </div>

                                <div className={styles.dropDownWrapper}>
                                    <Select
                                        className={styles.customReactSelectDropdown}
                                        options={filterRoleList || []}
                                        multi
                                        onSelect={() => null}
                                        onDeselect={() => null}
                                        wrapperClassName={styles.customReactSelectInput}
                                        closeOnClickInput
                                        values={filterRoleList?.filter((item) => filterState?.roles?.includes(item.value)) || []}
                                        keepSelectedInList
                                        onChange={(values: Option[]) => {
                                            if (values) {
                                                setFilterState({
                                                    ...filterState,
                                                    roles: values.map((value: Option) => value.value),
                                                });
                                            }
                                        }}
                                        searchBy="label"
                                        clearable={false}
                                        contentRenderer={customRoleContentRenderer}
                                        itemRenderer={itemRenderer}
                                        dropdownRenderer={customDropdownRenderer}
                                    // onSelect={() => undefined}
                                    // onDeselect={() => undefined}
                                    />
                                </div>

                                <div className={styles.filterBtnWrapper}>
                                    <button
                                        className={styles.outlinedBtn}
                                        onClick={() =>
                                            JSON.stringify(filterState) !==
                                                JSON.stringify({
                                                    properties: [],
                                                    roles: [],
                                                })
                                                ? onClear()
                                                : onCancel()
                                        }>
                                        {JSON.stringify(filterState) !==
                                            JSON.stringify({
                                                properties: [],
                                                roles: [],
                                            })
                                            ? `Clear`
                                            : `Cancel`}
                                    </button>
                                    <button
                                        className={styles.btnGreen}
                                        onClick={() => {
                                            onFilter();
                                        }}>
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <SearchInput
                        value={searchTerm}
                        placeholder="Search Staff"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: 264 }}
                    />

                    <Button
                        variant="green"
                        onClick={() => {
                            setShowModal(true);
                            setSelectedId(null);
                        }}>
                        <PlusIcon />
                        <span>Add Staff</span>
                    </Button>
                </div>
            </div>

            <div className={styles.tableContainer}>
                <div className={styles.tableListing}>
                    <table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role Name</th>
                                <th>Assigned Properties</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {staffList?.length > 0
                                ? staffList.map((staff: StaffMember, index: number) => {
                                    const filteredLocations = staff?.locations?.filter((location: LocationListProps) =>
                                        locationList?.some((loc) => loc.id === location.id),
                                    );
                                    const isLastElement = index === staffList.length - 1;
                                    return (
                                        <tr
                                            className={staff?.isActive ? '' : styles.deactivatedStaff}
                                            key={index}
                                            ref={(ref) => {
                                                actionButtonRefs.current[index] = ref;
                                                if (isLastElement && ref) {
                                                    lastElement(ref);
                                                }
                                            }}>
                                            <td style={{ textTransform: 'capitalize' }}>{staff?.name}</td>
                                            <td>{staff?.email || ''}</td>
                                            <td>{staff?.role || '-'}</td>
                                            <td className={styles.locations}>
                                                <div className={styles.locationsContainer}>
                                                    {filteredLocations && filteredLocations.length > 0 ? (
                                                        <>
                                                            <div className={styles.locationsList}>
                                                                {filteredLocations.slice(0, 3).map((location) => (
                                                                    <div key={location.id} className={styles.locationItem}>
                                                                        {location.name}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {filteredLocations.length > 3 && (
                                                                <div className={styles.locationsFooter}>
                                                                    <button
                                                                        className={styles.showMoreButton}
                                                                        onClick={() => handleShowProperties(staff)}>
                                                                        +{filteredLocations.length - 3} more
                                                                    </button>
                                                                    {/* <span className={styles.locationsCount}>
                                                                        ({filteredLocations.length} properties)
                                                                    </span> */}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div>-</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {!!showActionBox && (
                                                    <div className={styles.dropDownBackDrop} onClick={() => setShowActionBox('')}></div>
                                                )}
                                                <div className={styles.actionButtonWrapper}>
                                                    <button
                                                        className={styles.MageDotsIcon}
                                                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                            event.stopPropagation();
                                                            handleActionButtonClick(index?.toString());
                                                        }}>
                                                        <MageDotsIcon />
                                                    </button>
                                                    <div
                                                        className={`${styles.actionContainer} ${index?.toString() === showActionBox ? styles.active : ''
                                                            } ${actionBoxPosition === 'top' ? styles.top : ''}`}>
                                                        <div
                                                            className={styles.actionBox}
                                                            role="button"
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();
                                                                setSelectedId(staff?.id);
                                                                setShowModal(true);
                                                                setShowActionBox(null);
                                                                // onEditStaffRequest(staff?.id);
                                                            }}>
                                                            <span>{'Edit'}</span>
                                                        </div>
                                                        <div
                                                            className={styles.actionBox}
                                                            role="button"
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();
                                                                setShowActionBox(null);
                                                                staff?.isActive ? deactivate(staff?.id) : activate(staff?.id);
                                                            }}>
                                                            <span>{staff?.isActive ? 'Deactivate' : 'Activate'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                                : !staffListLoading && (
                                    <tr>
                                        <td style={{ textAlign: 'center' }} colSpan={5}>
                                            No Records
                                        </td>
                                    </tr>
                                )}
                        </tbody>
                        {staffListLoading && staffListData?.length > 0 && (
                            <tfoot>
                                <tr>
                                    <td colSpan={5} className={styles.tinyLoaderCell}>
                                        <div className={styles.tinyLoader} ref={tinyLoaderRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                            <TinyLoader />
                                            <span style={{ fontSize: '14px', color: '#666' }}>Fetching more data...</span>
                                        </div>
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
            <CustModal
                open={showModal}
                bodyData={
                    <StaffModal
                        onClose={() => setShowModal(false)}
                        locationList={locationList}
                        rolesList={rolesList}
                        selectedId={selectedId || ''}
                        setSelectedId={setSelectedId}
                        onSuccess={() => getUpdatedStaffList()}
                        setShowModal={setShowModal}
                        setShowToast={setShowToast}
                        setToastMessage={setToastMessage}
                        setToastType={setToastType}
                    />
                }
                className={styles.staffModal}
                onCloseModal={() => {
                    setShowModal(false);
                }}
                shouldCloseOnOverLay={false}
            />

            <PropertiesModal
                isOpen={showPropertiesModal}
                onClose={() => {
                    setShowPropertiesModal(false);
                    setSelectedStaffForProperties(null);
                }}
                propertyData={selectedStaffForProperties}
            />

            {/* ── Import Result Modal ── */}
            {showImportResultModal && (
                <div className={styles.importResultBackdrop} onClick={() => setShowImportResultModal(false)}>
                    <div className={styles.importResultModal} onClick={(e) => e.stopPropagation()}>

                        {/* Header */}
                        <div className={styles.importResultHeader}>
                            <h3>Bulk Import Results</h3>
                            <button
                                className={styles.importResultClose}
                                onClick={() => setShowImportResultModal(false)}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Two-column summary cards */}
                        <div className={styles.importResultCards}>
                            <div className={styles.importResultCardSuccess}>
                                <div className={styles.importResultCardIcon}>✓</div>
                                <div className={styles.importResultCardCount}>{successCount}</div>
                                <div className={styles.importResultCardLabel}>Staff Imported Successfully</div>
                            </div>
                            <div className={styles.importResultCardFailed}>
                                <div className={styles.importResultCardIcon}>✕</div>
                                <div className={styles.importResultCardCount}>{failedData.length}</div>
                                <div className={styles.importResultCardLabel}>Staff Failed to Import</div>
                            </div>
                        </div>

                        {/* Failed staff list */}
                        {failedData.length > 0 && (
                            <>
                                <div className={styles.importResultListHeader}>
                                    <span>Failed Records</span>
                                </div>
                                <div className={styles.importResultList}>
                                    <div className={styles.importResultListHeadRow}>
                                        <span>Email</span>
                                        <span>Reason</span>
                                    </div>
                                    {failedData.map((item, idx) => (
                                        <div key={idx} className={styles.importResultListRow}>
                                            <span className={styles.importResultEmail}>{item.email || '—'}</span>
                                            <span className={styles.importResultReason}>{item.reason || '—'}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Download button */}
                                <div className={styles.importResultFooter}>
                                    <Button
                                        variant="outlined-red"
                                        onClick={() => {
                                            downloadFailedCSV();
                                            setShowImportResultModal(false);
                                        }}
                                    >
                                        <span>Download Failed Data</span>
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showToast && toastMessage && <Toast type={toastType} message={toastMessage} />}
        </div>
    );
};

export default StaffManagement;
